import { useEffect, useRef, useState } from 'react';
import Invoice from '../invoice/Invoice';
import styles from './payment-methods.module.less';
import { useProject } from '../../project-hooks/useProject';
import { Heading2 } from '@sb1/ffe-core-react';
import { RadioButton, RadioButtonInputGroup } from '@sb1/ffe-form-react';
import {
    paymentMethodCardIllustration,
    paymentMethodLabel,
    paymentMethodTitle,
    paymentMethodTrackingLabel,
} from './paymentMethodUtils';
import { trackEvent, trackPixelEvent } from '../../../support-and-tracking/analytics-actions';
import { useSubproject } from '../../../components/hooks/useSubproject';
import { getFormActions } from '../../../utils/form-helpers';
import invoiceFormConfig from '../../../payment/invoice-form-config';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '../../../user/useUser';
import RewardInfoForm from '../reward-info-form/RewardInfoForm';
import stripeFormConfig, { canSubmit } from '../../../payment/stripe-form-config';
import rewardInfoFormConfig, { getGiverAddress } from '../../../payment/reward-giver-info-form-config';
import isEmpty from 'lodash/isEmpty';
import { doesAnyRewardNeedAddressOrPhonenumber, mapCartItemForPayment } from '../cart/cart-model';
import { getVippsForm } from '../../../payment/payment-selectors';
import { useNavigate, useSearchParams } from 'react-router';
import { initiateVippsPayment } from '../../../payment/vipps-actions';
import Stripe from '../stripe/Stripe';
import { createInvoice } from '../../../payment/invoice-actions';
import vippsFormConfig from '../../../payment/vipps-form-config';
import { PrimaryButton } from '@sb1/ffe-buttons-react';
import PaymentErrorMessage from './PaymentErrorMessage';
import { isUserUsingNorwegianLanguageOnProject, PAYMENT_OPTIONS_TEXT_KEY } from '../../i18n/project-i18n-model';
import I18nText from '../../i18n/I18nText';
import { getCartItems, getCartSum } from '../../../cart/cart-reducer';
import { OptInPersonalizedNewsletterCheck } from '../give-support/OptInPersonalizedNewsletterCheck';
import { projectHasGiverFee } from '../../project-model';
import { ContextMessage } from '@sb1/ffe-messages-react';
import { useFeature } from '../../../components/hooks/isFeatureEnabled';
import { PAYMENT_METHODS_BANNER } from '../../../constants/features/constants';

export const PAYMENT_METHOD_VIPPS = 'PAYMENT_METHOD_VIPPS';
export const PAYMENT_METHOD_STRIPE = 'PAYMENT_METHOD_STRIPE';
export const PAYMENT_METHOD_INVOICE = 'PAYMENT_METHOD_INVOICE';
export const paymentMethods = [PAYMENT_METHOD_VIPPS, PAYMENT_METHOD_STRIPE, PAYMENT_METHOD_INVOICE];

const rewardFormActions = getFormActions(rewardInfoFormConfig);
const stripeFormActions = getFormActions(stripeFormConfig);
const invoiceFormActions = getFormActions(invoiceFormConfig);
const vippsFormActions = getFormActions(vippsFormConfig);

const PaymentMethods = ({
    isCart = false,
    giverName,
    comment,
    commentImageUrl,
    memorialCommentId,
    validatePaymentAmount,
    availablePaymentMethods,
    amountError,
}) => {
    const stripeRef = useRef();
    const project = useProject();
    const { subproject } = useSubproject();
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const user = useUser();
    const navigate = useNavigate();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
        availablePaymentMethods && availablePaymentMethods[0]
    );
    const paymentMethodsBannerFeature = useFeature(PAYMENT_METHODS_BANNER);
    const [paymentButtonClicked, setPaymentButtonClicked] = useState(false);
    const [consentedToPersonalizedNewsletter, setConsentedToPersonalizedNewsletter] = useState(false);
    const rewardGiverInfoForm = useSelector((state) => state.rewardGiverInfoForm);
    const vippsForm = useSelector((state) => getVippsForm(state));
    const stripeForm = useSelector((state) => state.stripeForm);
    const invoiceForm = useSelector((state) => state.invoiceForm);
    const paymentForm = useSelector((state) => state.paymentForm);
    const cartItems = useSelector((state) => getCartItems(state, project.id)) || [];
    const cartSum = useSelector((state) => getCartSum(state, project.id));
    const rewards = cartItems.map((item) => mapCartItemForPayment(item, project));
    const totalAmount = (cartSum || 0) + (paymentForm.amount || 0) + (project?.staticGiverFee || 0);

    const shouldShowRewardAddressForm =
        doesAnyRewardNeedAddressOrPhonenumber({ rewards }) && !rewardGiverInfoForm.hasEnteredAddress;
    const userIsUsingNorwegianLanguage = isUserUsingNorwegianLanguageOnProject(project);

    const personalizedNewsletterOnChange = (consent) => {
        trackEvent('support', 'personalized-newsletter-opt-in-change', {
            project_id: project?.id,
            category: project?.categories?.[0],
            is_checked: consent,
        });
        setConsentedToPersonalizedNewsletter(consent);
    };

    useEffect(() => {
        return () => {
            clearAllData();
        };
    }, []);

    const projectPollOptionIdString = searchParams.get('projectPollOptionId');
    const projectPollOptionId = projectPollOptionIdString ? parseInt(projectPollOptionIdString, 10) : undefined;

    const trackingProps = {
        'project-id': project.id,
        'subproject-id': subproject?.id,
        amount: totalAmount,
        cart: isCart,
        reward: rewards?.length > 0,
        giverName: !!giverName,
        comment: !!comment,
        commentImage: !!commentImageUrl,
        projectHasGiverFee: projectHasGiverFee(project),
    };
    const trackingPropsExtras = {
        did_consent_to_personalized_newsletter: consentedToPersonalizedNewsletter,
        category: project?.categories?.[0],
    };

    const clearAllData = () => {
        dispatch(invoiceFormActions.clearAll());
        dispatch(rewardFormActions.clearAll());
        dispatch(stripeFormActions.clearAll());
        dispatch(vippsFormActions.clearAll());
    };

    const startCreateInvoice = () => {
        dispatch(
            invoiceFormActions.setFormFields({
                projectId: project?.id,
                subprojectId: subproject?.id,
                giverName,
                comment,
                commentImageUrl,
            })
        );
        const errors = dispatch(invoiceFormActions.validateForm());
        if (isEmpty(errors)) {
            const rewardGiverInfoMetadata = doesAnyRewardNeedAddressOrPhonenumber({ rewards })
                ? getGiverAddress(rewardGiverInfoForm)
                : null;
            dispatch(invoiceFormActions.setFormField('isCreatingInvoice', true));

            dispatch(
                createInvoice({
                    totalAmount,
                    rewardGiverInfoMetadata,
                    consentedToPersonalizedNewsletter,
                    memorialCommentId,
                })
            )
                .then((invoice) => {
                    trackPixelEvent('Purchase', {
                        value: totalAmount,
                        currency: 'NOK',
                    });
                    trackEvent('support', 'checkout-complete', {
                        method: 'Invoice',
                        ...trackingProps,
                    });
                    navigate(`/project/${project.id}/invoice-registered/${invoice.body.id}/${invoice.body.nonce}`);
                })
                .catch((error) => {
                    dispatch(invoiceFormActions.setFormFields({ isCreatingInvoice: false }));
                    dispatch(invoiceFormActions.setErrors(error.body?.errors));
                });
        }
    };

    const startVippsPayment = () => {
        if (!project) {
            return null;
        }
        const initiateVippsData = {
            projectId: project?.id,
            subprojectId: subproject?.id,
            projectPollOptionId,
            amount: totalAmount,
            rewards,
            nameAndComment: {
                giverName,
                comment,
                commentImageUrl,
                anonymous: giverName ? false : undefined,
            },
            consentedToPersonalizedNewsletter,
            memorialCommentId,
        };
        dispatch(initiateVippsPayment(initiateVippsData));
    };

    const startStripePayment = () => {
        if (!canSubmit(stripeForm)) {
            dispatch(stripeFormActions.validateForm());
            return;
        }
        if (stripeRef.current) {
            stripeRef.current.submit();
        }
    };

    const setInvoiceForm = () => {
        if (user.id) {
            dispatch(invoiceFormActions.setFormField('email', user.email));
        }
    };

    const selectPaymentMethod = async (paymentMethod) => {
        await clearAllData();
        trackEvent('support', 'checkout-provider', {
            method: paymentMethodTrackingLabel(selectedPaymentMethod),
            'project-id': project.id,
            ...trackingProps,
        });
        switch (paymentMethod) {
            case PAYMENT_METHOD_INVOICE:
                setInvoiceForm();
                break;
            case PAYMENT_METHOD_STRIPE:
                if (user.id) {
                    dispatch(stripeFormActions.setFormField('email', user.email));
                }
                break;
        }
        setSelectedPaymentMethod(paymentMethod);
    };

    const initiatePayment = () => {
        setPaymentButtonClicked(true);
        if (!vippsSelected && shouldShowRewardAddressForm) {
            dispatch(rewardFormActions.validateForm());
        }

        if (!validatePaymentAmount(selectedPaymentMethod) || !isEmpty(rewardGiverInfoForm.errors)) {
            return;
        }

        trackPixelEvent('InitiateCheckout');
        trackEvent('support', 'checkout-pay', {
            method: paymentMethodTrackingLabel(selectedPaymentMethod),
            ...trackingProps,
            ...trackingPropsExtras,
        });

        switch (selectedPaymentMethod) {
            case PAYMENT_METHOD_INVOICE:
                startCreateInvoice();
                break;
            case PAYMENT_METHOD_STRIPE:
                startStripePayment();
                break;
            case PAYMENT_METHOD_VIPPS:
                startVippsPayment();
                break;
        }
    };

    const vippsSelected = selectedPaymentMethod === PAYMENT_METHOD_VIPPS;
    const stripeSelected = selectedPaymentMethod === PAYMENT_METHOD_STRIPE;
    const invoiceSelected = selectedPaymentMethod === PAYMENT_METHOD_INVOICE;

    const isLoading =
        stripeForm?.isProcessingPayment || vippsForm?.isProcessingPayment || invoiceForm?.isCreatingInvoice;

    const moreThanOnePaymentMethod = availablePaymentMethods.length > 1;
    return (
        <div className={styles.paymentMethodsContainer}>
            {(moreThanOnePaymentMethod || availablePaymentMethods.length === 0) && (
                <div>
                    {moreThanOnePaymentMethod && (
                        <Heading2 lookLike={3} className={styles.paymentMethodsTitle} noMargin={true}>
                            <I18nText textKey={PAYMENT_OPTIONS_TEXT_KEY} />
                        </Heading2>
                    )}
                    {paymentMethodsBannerFeature && (
                        <ContextMessage type={'warning'} compact style={{ marginBottom: '1rem' }}>
                            {paymentMethodsBannerFeature}
                        </ContextMessage>
                    )}

                    {moreThanOnePaymentMethod && (
                        <RadioButtonInputGroup
                            className={styles.cardContainer}
                            name="choose-payment-method"
                            onChange={(e) => {
                                selectPaymentMethod(e.target.value);
                            }}
                            selectedValue={selectedPaymentMethod}
                        >
                            {(inputProps) =>
                                availablePaymentMethods.map((method) => (
                                    <div key={method} className={styles.radioButtonsWithIllustration}>
                                        <RadioButton value={method} {...inputProps}>
                                            {paymentMethodLabel(method)}
                                        </RadioButton>
                                        {paymentMethodCardIllustration(method)}
                                    </div>
                                ))
                            }
                        </RadioButtonInputGroup>
                    )}
                    {availablePaymentMethods.length === 0 && (
                        <span>Betaling er ikke tilgjengelig akkurat nå, vennligst kom innom her senere!</span>
                    )}
                </div>
            )}
            {!vippsSelected && (
                <div className={styles.cardContainer}>
                    {shouldShowRewardAddressForm && <RewardInfoForm rewards={rewards} />}
                    {stripeSelected && (
                        <Stripe
                            ref={stripeRef}
                            giverName={giverName}
                            comment={comment}
                            commentImageUrl={commentImageUrl}
                            memorialCommentId={memorialCommentId}
                            consentedToPersonalizedNewsletter={consentedToPersonalizedNewsletter}
                            totalAmount={totalAmount}
                            rewards={rewards}
                            userIsUsingNorwegianLanguage={userIsUsingNorwegianLanguage}
                        />
                    )}
                    {invoiceSelected && <Invoice />}
                </div>
            )}
            {isUserUsingNorwegianLanguageOnProject(project) && (
                <OptInPersonalizedNewsletterCheck
                    consentedToPersonalizedNewsletter={consentedToPersonalizedNewsletter}
                    personalizedNewsletterOnChange={personalizedNewsletterOnChange}
                />
            )}
            {paymentButtonClicked && <PaymentErrorMessage amountError={amountError} />}
            <PrimaryButton
                className={[
                    selectedPaymentMethod === PAYMENT_METHOD_VIPPS && styles.vippsPaymentButton,
                    styles.paymentButton,
                ].join(' ')}
                ariaLoadingMessage="Vennligst vent..."
                onClick={initiatePayment}
                isLoading={isLoading}
                size="lg"
            >
                {paymentMethodTitle(selectedPaymentMethod)}
            </PrimaryButton>
        </div>
    );
};

export default PaymentMethods;
